const API_BASE = '/api';

const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function request(path, options = {}, retryCount = 0) {
  const token = localStorage.getItem('token');
  const pinToken = localStorage.getItem('pinSession');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(pinToken ? { 'X-Pin-Session': pinToken } : {}),
  };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  const config = {
    ...options,
    headers: { ...headers, ...options.headers },
    signal: controller.signal,
  };

  try {
    const res = await fetch(`${API_BASE}${path}`, config);
    clearTimeout(timeoutId);

    // Handle blob responses (Excel export)
    if (res.headers.get('content-type')?.includes('spreadsheet')) {
      if (!res.ok) throw new Error('Export failed');
      return res.blob();
    }

    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || 'Request failed');
      err.responseBody = data; // expose full payload so callers can handle structured errors (e.g., blocked stages)
      err.status = res.status;
      throw err;
    }
    return data;
  } catch (err) {
    clearTimeout(timeoutId);

    // Determine if retryable
    const isTimeout = err.name === 'AbortError';
    const isNetworkError = err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.';
    const isRetryable = isTimeout || isNetworkError;

    if (isRetryable && retryCount < MAX_RETRIES) {
      await sleep(RETRY_DELAY * (retryCount + 1));
      return request(path, options, retryCount + 1);
    }

    if (isTimeout) {
      throw new Error('Request timed out — please try again');
    }

    throw err;
  }
}

function qs(params) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  const s = p.toString();
  return s ? '?' + s : '';
}

export const api = {
  // Auth
  login: (username, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  getMe: () => request('/auth/me'),

  // Users
  getUsers: () => request('/users'),
  getUser: (id) => request(`/users/${id}`),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deactivateUser: (id) => request(`/users/${id}/deactivate`, { method: 'PATCH' }),
  reactivateUser: (id) => request(`/users/${id}/reactivate`, { method: 'PATCH' }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  uploadUserPhoto: (id, formData) => request(`/users/${id}/photo`, { method: 'POST', body: formData }),
  deleteUserPhoto: (id) => request(`/users/${id}/photo`, { method: 'DELETE' }),

  // Notifiable users (for Sales Rep to Notify dropdown)
  getNotifiableUsers: () => request('/users/notifiable'),

  // Active users (for history filter dropdowns)
  getActiveUsers: () => request('/users/active-list'),

  // Accounts Migration (super_admin only) — properties → accounts cutover
  getMigrationStats: () => request('/admin/migration/stats'),
  getMigrationFlags: () => request('/admin/migration/flags'),
  setMigrationFlag: (key, enabled) => request(`/admin/migration/flags/${key}`, {
    method: 'PATCH', body: JSON.stringify({ enabled }),
  }),
  getMigrationAuditLog: (params = {}) => {
    const qsParts = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    const suffix = qsParts.length ? `?${qsParts.join('&')}` : '';
    return request(`/admin/migration/audit-log${suffix}`);
  },
  runMigrationReconcile: () => request('/admin/migration/reconcile', { method: 'POST' }),
  backfillRequestsAccountIds: () => request('/admin/migration/backfill-requests', { method: 'POST' }),
  backfillExpensesAccountIds: () => request('/admin/migration/backfill-expenses', { method: 'POST' }),
  backfillAuditsAccountIds: () => request('/admin/migration/backfill-audits', { method: 'POST' }),

  // Accounts 360 — read-only unified account view (Step 2)
  listAccounts360: (params = {}) => {
    const qsParts = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    const suffix = qsParts.length ? `?${qsParts.join('&')}` : '';
    return request(`/account-360/list${suffix}`);
  },
  getAccount360: (id) => request(`/account-360/${id}`),

  // Modules
  getModules: () => request('/modules'),
  updateModule: (key, data) => request(`/modules/${key}`, { method: 'PUT', body: JSON.stringify(data) }),
  reorderModules: (order) => request('/modules/reorder/bulk', { method: 'PUT', body: JSON.stringify({ order }) }),

  // Module Setup (per-module config)
  getModuleCatalog: (moduleKey) => request(`/module-setup/${moduleKey}/catalog`),
  getModulePermissions: (moduleKey) => request(`/module-setup/${moduleKey}/permissions`),
  updateModulePermissions: (moduleKey, overrides) => request(`/module-setup/${moduleKey}/permissions`, { method: 'PUT', body: JSON.stringify({ overrides }) }),
  getModuleAccess: (moduleKey) => request(`/module-setup/${moduleKey}/access`),
  updateModuleAccess: (moduleKey, rules) => request(`/module-setup/${moduleKey}/access`, { method: 'PUT', body: JSON.stringify({ rules }) }),
  getModuleScope: (moduleKey) => request(`/module-setup/${moduleKey}/scope`),
  getModuleNotificationEvents: (moduleKey) => request(`/module-setup/${moduleKey}/notification-events`),
  updateNotificationEventFilter: (moduleKey, eventId, filter) =>
    request(`/module-setup/${moduleKey}/notification-events/${eventId}`, {
      method: 'PUT', body: JSON.stringify({ filter }),
    }),
  toggleNotificationEventEnabled: (moduleKey, eventId, is_enabled) =>
    request(`/module-setup/${moduleKey}/notification-events/${eventId}/enabled`, {
      method: 'PUT', body: JSON.stringify({ is_enabled }),
    }),
  getModuleConfigurators: (moduleKey) => request(`/module-setup/${moduleKey}/configurators`),
  updateModuleConfigurators: (moduleKey, user_ids) => request(`/module-setup/${moduleKey}/configurators`, { method: 'PUT', body: JSON.stringify({ user_ids }) }),

  // Drivers
  getDrivers: () => request('/drivers'),
  getDriverProfile: (id) => request(`/drivers/${id}/profile`),
  updateDriverProfile: (id, data) => request(`/drivers/${id}/profile`, { method: 'PUT', body: JSON.stringify(data) }),

  // Public stats (no auth needed)
  getPublicStats: () => fetch('/api/public/stats').then(r => r.ok ? r.json() : null).catch(() => null),

  // Calls
  getCalls: (filters = {}) => request(`/calls${qs(filters)}`),
  getCallHistory: (filters = {}) => request(`/calls/history${qs(filters)}`),
  getCallHistoryStats: (filters = {}) => request(`/calls/history/stats${qs(filters)}`),
  exportCallHistory: (filters = {}) => request(`/calls/history/export${qs(filters)}`),
  getScheduledCalls: (filters = {}) => request(`/calls/scheduled${qs(filters)}`),
  getPendingCalls: (filters = {}) => request(`/calls/pending${qs(filters)}`),
  approveCall: (id) => request(`/calls/${id}/approve`, { method: 'PATCH' }),
  rejectCall: (id, reason) => request(`/calls/${id}/reject`, { method: 'PATCH', body: JSON.stringify({ rejection_reason: reason }) }),
  getCall: (id) => request(`/calls/${id}`),
  createCall: (formData) => request('/calls', { method: 'POST', body: formData }),
  updateCall: (id, data) => request(`/calls/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  claimCall: (id) => request(`/calls/${id}/claim`, { method: 'PATCH' }),
  unclaimCall: (id) => request(`/calls/${id}/unclaim`, { method: 'PATCH' }),
  resolveCall: (id, formData) => request(`/calls/${id}/resolve`, { method: 'PATCH', body: formData }),
  deleteCall: (id) => request(`/calls/${id}`, { method: 'DELETE' }),
  addCallPhotos: (id, formData) => request(`/calls/${id}/photos`, { method: 'POST', body: formData }),
  deleteCallPhoto: (id, photoId) => request(`/calls/${id}/photos/${photoId}`, { method: 'DELETE' }),

  // Call Comments
  getCallComments: (callId) => request(`/calls/${callId}/comments`),
  addCallComment: (callId, message) => request(`/calls/${callId}/comments`, {
    method: 'POST', body: JSON.stringify({ message }),
  }),

  // Properties
  getProperties: (filters = {}) => request(`/properties${qs(filters)}`),
  getProperty: (id) => request(`/properties/${id}`),
  getPropertyKpis: (id, params = {}) => request(`/properties/${id}/kpis${qs(params)}`),
  getPropertyActivity: (id, params = {}) => request(`/properties/${id}/activity${qs(params)}`),

  // Storage admin
  getStorageReport: () => request('/admin/storage/report'),
  runStorageSweep: () => request('/admin/storage/sweep', { method: 'POST' }),
  runStorageCompact: () => request('/admin/storage/compact', { method: 'POST' }),
  getPhotoTierEstimate: () => request('/admin/storage/photos/estimate'),
  runPhotoTierSweep: () => request('/admin/storage/photos/sweep', { method: 'POST' }),

  // Data Pull Log (super admin)
  getDataPullOverview: () => request('/admin/data-pulls/overview'),
  getDataPullRuns: (params = {}) => request(`/admin/data-pulls/runs${qs(params)}`),
  getDataPullCandidates: (source) => request(`/admin/data-pulls/candidates/${source}`),
  matchDataPullIssue: (id, data) => request(`/admin/data-pulls/issues/${id}/match`, { method: 'POST', body: JSON.stringify(data) }),
  markDataPullIssueFixed: (id, note = '') => request(`/admin/data-pulls/issues/${id}/fixed`, { method: 'POST', body: JSON.stringify({ note }) }),
  runDataPull: (source) => request(`/admin/data-pulls/run/${source}`, { method: 'POST' }),

  // Grading admin
  getGradingThresholds: () => request('/admin/grading/thresholds'),
  saveGradingThresholds: (kpi, rows) => request(`/admin/grading/thresholds/${kpi}`, {
    method: 'PUT', body: JSON.stringify({ rows }),
  }),
  getGradingWeights: () => request('/admin/grading/weights'),
  saveGradingWeights: (rows) => request('/admin/grading/weights', {
    method: 'PUT', body: JSON.stringify({ rows }),
  }),
  getGradingCities: () => request('/admin/grading/city-constraints'),
  createGradingCity: (data) => request('/admin/grading/city-constraints', {
    method: 'POST', body: JSON.stringify(data),
  }),
  updateGradingCity: (id, data) => request(`/admin/grading/city-constraints/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  deleteGradingCity: (id) => request(`/admin/grading/city-constraints/${id}`, { method: 'DELETE' }),
  getGradingActions: () => request('/admin/grading/actions'),
  saveGradingActions: (kpi, rows) => request(`/admin/grading/actions/${kpi}`, {
    method: 'PUT', body: JSON.stringify({ rows }),
  }),

  // Per-property tow counts
  getPropertyTowCounts: (propertyId) => request(`/admin/grading/tow-counts/${propertyId}`),
  setPropertyTowCount: (propertyId, year, month, tow_count, notes) => request(
    `/admin/grading/tow-counts/${propertyId}/${year}/${month}`,
    { method: 'PUT', body: JSON.stringify({ tow_count, notes: notes || null }) }
  ),
  deletePropertyTowCount: (propertyId, year, month) => request(
    `/admin/grading/tow-counts/${propertyId}/${year}/${month}`,
    { method: 'DELETE' }
  ),

  // Per-property tow fee (quick-set without full property edit)
  setPropertyTowFee: (propertyId, tow_fee) => request(
    `/admin/grading/tow-fee/${propertyId}`,
    { method: 'PUT', body: JSON.stringify({ tow_fee }) }
  ),

  // Bulk-import tow counts (CSV, Parking Snap export format)
  bulkImportTowCounts: (formData, dryRun = false) => request(
    `/admin/grading/tow-counts/bulk-import${dryRun ? '?dry_run=1' : ''}`,
    { method: 'POST', body: formData }
  ),

  // Cross-property report
  getGradingReport: (params = {}) => request(`/admin/grading/report${qs(params)}`),
  downloadGradingReport: async (params = {}) => {
    const res = await fetch(`${API_BASE}/admin/grading/report/export${qs(params)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const cd = res.headers.get('content-disposition') || '';
    const match = cd.match(/filename="([^"]+)"/);
    const a = document.createElement('a');
    a.href = url;
    a.download = match ? match[1] : `grading-report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  createProperty: (data) => request('/properties', { method: 'POST', body: JSON.stringify(data) }),
  updateProperty: (id, data) => request(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProperty: (id) => request(`/properties/${id}`, { method: 'DELETE' }),
  uploadProperties: (formData) => request('/properties/upload', { method: 'POST', body: formData }),
  downloadPropertyTemplate: () => request('/properties/template'),
  downloadRegionChangeList: () => request('/properties/region-change/template'),
  previewRegionChange: (formData) => request('/properties/region-change/preview', { method: 'POST', body: formData }),
  applyRegionChange: (changes, move_open_calls) => request('/properties/region-change/apply', { method: 'POST', body: JSON.stringify({ changes, move_open_calls }) }),

  // Config lists
  getCallTypes: () => request('/call-types'),
  createCallType: (data) => request('/call-types', { method: 'POST', body: JSON.stringify(data) }),
  updateCallType: (id, data) => request(`/call-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCallType: (id) => request(`/call-types/${id}`, { method: 'DELETE' }),

  getCallReasons: () => request('/call-reasons'),
  createCallReason: (data) => request('/call-reasons', { method: 'POST', body: JSON.stringify(data) }),
  updateCallReason: (id, data) => request(`/call-reasons/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCallReason: (id) => request(`/call-reasons/${id}`, { method: 'DELETE' }),

  getResolutionReasons: () => request('/resolution-reasons'),
  createResolutionReason: (data) => request('/resolution-reasons', { method: 'POST', body: JSON.stringify(data) }),
  updateResolutionReason: (id, data) => request(`/resolution-reasons/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteResolutionReason: (id) => request(`/resolution-reasons/${id}`, { method: 'DELETE' }),

  // VSF Locations (Take Back pickup locations)
  getVsfLocations: () => request('/vsf-locations'),
  createVsfLocation: (data) => request('/vsf-locations', { method: 'POST', body: JSON.stringify(data) }),
  updateVsfLocation: (id, data) => request(`/vsf-locations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteVsfLocation: (id) => request(`/vsf-locations/${id}`, { method: 'DELETE' }),

  // Photos
  uploadPhotos: (formData) => request('/photos', { method: 'POST', body: formData }),
  deletePhoto: (id) => request(`/photos/${id}`, { method: 'DELETE' }),

  // Settings
  getSettings: () => request('/settings'),
  updateSettings: (settings) => request('/settings', { method: 'PUT', body: JSON.stringify({ settings }) }),

  // Public config (no auth needed)
  getPublicConfig: () => fetch(`${API_BASE}/public/config`).then(r => r.json()),

  // Push notifications
  getVapidKey: () => request('/push/vapid-key'),
  subscribePush: (subscription) =>
    request('/push/subscribe', { method: 'POST', body: JSON.stringify({ subscription }) }),
  unsubscribePush: (endpoint) =>
    request('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),

  // Trucks
  getTrucks: () => request('/trucks'),
  getTruck: (id) => request(`/trucks/${id}`),
  createTruck: (data) => request('/trucks', { method: 'POST', body: JSON.stringify(data) }),
  updateTruck: (id, data) => request(`/trucks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTruck: (id) => request(`/trucks/${id}`, { method: 'DELETE' }),

  // Shops
  getShops: () => request('/shops'),
  createShop: (data) => request('/shops', { method: 'POST', body: JSON.stringify(data) }),
  updateShop: (id, data) => request(`/shops/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteShop: (id) => request(`/shops/${id}`, { method: 'DELETE' }),

  // Inspections
  getInspections: (filters = {}) => request(`/inspections${qs(filters)}`),
  getInspection: (id) => request(`/inspections/${id}`),
  getInspectionItems: () => request('/inspections/items'),
  // Admin: list active + archived items, create, update (toggle is_critical etc.)
  getAllInspectionItems: () => request('/inspections/items/all'),
  createInspectionItem: (data) => request('/inspections/items', { method: 'POST', body: JSON.stringify(data) }),
  updateInspectionItem: (id, data) => request(`/inspections/items/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getMyInspectionStatus: () => request('/inspections/my-status'),
  getInspectionCompliance: () => request('/inspections/compliance'),
  submitInspection: (data) => request('/inspections', { method: 'POST', body: JSON.stringify(data) }),
  reviewInspection: (id) => request(`/inspections/${id}/review`, { method: 'PATCH' }),
  uploadInspectionPhotos: (id, formData) => request(`/inspections/${id}/photos`, { method: 'POST', body: formData }),

  // Work Orders
  getWorkOrders: (filters = {}) => request(`/work-orders${qs(filters)}`),
  getWorkOrder: (id) => request(`/work-orders/${id}`),
  getWorkOrderStats: () => request('/work-orders/stats'),
  createWorkOrder: (data) => request('/work-orders', { method: 'POST', body: JSON.stringify(data) }),
  updateWorkOrder: (id, data) => request(`/work-orders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  claimWorkOrder: (id) => request(`/work-orders/${id}/claim`, { method: 'PATCH' }),
  completeWorkOrder: (id, payload) => request(`/work-orders/${id}/complete`, { method: 'PATCH', body: JSON.stringify(payload) }),
  closeWorkOrder: (id, formData) => request(`/work-orders/${id}/close`, { method: 'PATCH', body: formData }),
  updateWorkOrderCost: (id, completion_cost) => request(`/work-orders/${id}/cost`, { method: 'PATCH', body: JSON.stringify({ completion_cost }) }),
  editWorkOrderCloseout: (id, formData) => request(`/work-orders/${id}/closeout`, { method: 'PATCH', body: formData }),

  // Maintenance History
  getInspectionHistory: (filters = {}) => request(`/maintenance-history/inspections${qs(filters)}`),
  getInspectionHistoryStats: (filters = {}) => request(`/maintenance-history/inspections/stats${qs(filters)}`),
  getWorkOrderHistory: (filters = {}) => request(`/maintenance-history/work-orders${qs(filters)}`),
  getWorkOrderHistoryStats: (filters = {}) => request(`/maintenance-history/work-orders/stats${qs(filters)}`),
  getIncidentHistory: (filters = {}) => request(`/maintenance-history/incidents${qs(filters)}`),
  getIncidentHistoryStats: (filters = {}) => request(`/maintenance-history/incidents/stats${qs(filters)}`),

  // Maintenance Types
  getMaintenanceTypes: () => request('/maintenance-types'),
  createMaintenanceType: (data) => request('/maintenance-types', { method: 'POST', body: JSON.stringify(data) }),
  updateMaintenanceType: (id, data) => request(`/maintenance-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMaintenanceType: (id) => request(`/maintenance-types/${id}`, { method: 'DELETE' }),

  // Maintenance Status
  getTruckMaintenanceStatus: (filters = {}) => request(`/maintenance-status${qs(filters)}`),
  getMaintenanceStatusSummary: () => request('/maintenance-status/summary'),

  // Work Order Comments
  getWorkOrderComments: (woId) => request(`/work-order-comments/${woId}/comments`),
  addWorkOrderComment: (woId, message) => request(`/work-order-comments/${woId}/comments`, {
    method: 'POST', body: JSON.stringify({ message }),
  }),

  // Incidents
  getIncidents: (filters = {}) => request(`/incidents${qs(filters)}`),
  getIncident: (id) => request(`/incidents/${id}`),
  getIncidentStats: () => request('/incidents/stats'),
  createIncident: (data) => request('/incidents', { method: 'POST', body: JSON.stringify(data) }),
  uploadIncidentPhotos: (id, formData) => request(`/incidents/${id}/photos`, { method: 'POST', body: formData }),
  reviewIncident: (id, notes) => request(`/incidents/${id}/review`, { method: 'PATCH', body: JSON.stringify({ review_notes: notes }) }),
  createIncidentWorkOrder: (id, data) => request(`/incidents/${id}/create-work-order`, { method: 'PATCH', body: JSON.stringify(data) }),
  resolveIncident: (id, notes) => request(`/incidents/${id}/resolve`, { method: 'PATCH', body: JSON.stringify({ review_notes: notes }) }),
  closeIncident: (id) => request(`/incidents/${id}/close`, { method: 'PATCH' }),

  // Admin Module — Employees
  getEmployees: (filters = {}) => request(`/employees${qs(filters)}`),
  getEmployee: (id) => request(`/employees/${id}`),
  createEmployee: (data) => request('/employees', { method: 'POST', body: JSON.stringify(data) }),
  updateEmployee: (id, data) => request(`/employees/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEmployee: (id) => request(`/employees/${id}`, { method: 'DELETE' }),

  // Admin Module — Companies
  getCompanies: () => request('/companies'),
  getCompany: (id) => request(`/companies/${id}`),
  createCompany: (data) => request('/companies', { method: 'POST', body: JSON.stringify(data) }),
  updateCompany: (id, data) => request(`/companies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Admin Module — Compliance Categories
  getComplianceCategories: (filters = {}) => request(`/compliance-categories${qs(filters)}`),
  createComplianceCategory: (data) => request('/compliance-categories', { method: 'POST', body: JSON.stringify(data) }),
  updateComplianceCategory: (id, data) => request(`/compliance-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteComplianceCategory: (id) => request(`/compliance-categories/${id}`, { method: 'DELETE' }),

  // Admin Module — Compliance Items
  getComplianceItems: (filters = {}) => request(`/compliance-items${qs(filters)}`),
  getComplianceItem: (id) => request(`/compliance-items/${id}`),
  getComplianceStats: () => request('/compliance-items/stats/summary'),
  createComplianceItem: (data) => request('/compliance-items', { method: 'POST', body: JSON.stringify(data) }),
  updateComplianceItem: (id, data) => request(`/compliance-items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteComplianceItem: (id) => request(`/compliance-items/${id}`, { method: 'DELETE' }),
  bulkCreateComplianceItems: (data) => request('/compliance-items/bulk', { method: 'POST', body: JSON.stringify(data) }),

  // Admin Module — Compliance Documents
  getComplianceDocuments: (itemId) => request(`/compliance-documents/${itemId}`),
  uploadComplianceDocument: (itemId, formData) => request(`/compliance-documents/${itemId}`, { method: 'POST', body: formData }),
  deleteComplianceDocument: (docId) => request(`/compliance-documents/${docId}`, { method: 'DELETE' }),

  // Admin Module — Compliance Notifications
  getComplianceNotifications: (filters = {}) => request(`/compliance-notifications${qs(filters)}`),
  getComplianceNotificationCount: () => request('/compliance-notifications/count'),
  markNotificationRead: (id) => request(`/compliance-notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request('/compliance-notifications/read-all', { method: 'PATCH' }),

  // Admin Module — Compliance Templates
  getComplianceTemplates: (filters = {}) => request(`/compliance-templates${qs(filters)}`),
  getComplianceTemplate: (id) => request(`/compliance-templates/${id}`),
  createComplianceTemplate: (data) => request('/compliance-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateComplianceTemplate: (id, data) => request(`/compliance-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteComplianceTemplate: (id) => request(`/compliance-templates/${id}`, { method: 'DELETE' }),
  applyComplianceTemplate: (id, data) => request(`/compliance-templates/${id}/apply`, { method: 'POST', body: JSON.stringify(data) }),

  // Admin Module — Audit Log
  getAuditLog: (filters = {}) => request(`/audit-log${qs(filters)}`),

  // Admin Module — Facilities
  getFacilities: (filters = {}) => request(`/facilities${qs(filters)}`),
  getFacility: (id) => request(`/facilities/${id}`),
  createFacility: (data) => request('/facilities', { method: 'POST', body: JSON.stringify(data) }),
  updateFacility: (id, data) => request(`/facilities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFacility: (id) => request(`/facilities/${id}`, { method: 'DELETE' }),

  // ── Leaderboard Module ─────────────────────────────────────
  // Setup (admin)
  getLeaderboardKpis: (filters = {}) => request(`/leaderboard/setup/kpis${qs(filters)}`),
  getLeaderboardKpi: (id) => request(`/leaderboard/setup/kpis/${id}`),
  createLeaderboardKpi: (data) => request('/leaderboard/setup/kpis', { method: 'POST', body: JSON.stringify(data) }),
  updateLeaderboardKpi: (id, data) => request(`/leaderboard/setup/kpis/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLeaderboardKpi: (id) => request(`/leaderboard/setup/kpis/${id}`, { method: 'DELETE' }),

  getLeaderboardGroups: (filters = {}) => request(`/leaderboard/setup/groups${qs(filters)}`),
  createLeaderboardGroup: (data) => request('/leaderboard/setup/groups', { method: 'POST', body: JSON.stringify(data) }),
  updateLeaderboardGroup: (id, data) => request(`/leaderboard/setup/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLeaderboardGroup: (id) => request(`/leaderboard/setup/groups/${id}`, { method: 'DELETE' }),

  getLeaderboardSettings: () => request('/leaderboard/setup/settings'),
  updateLeaderboardSettings: (settings) => request('/leaderboard/setup/settings', { method: 'PUT', body: JSON.stringify({ settings }) }),

  // Viewing
  getLeaderboard: (groupName, filters = {}) => request(`/leaderboard/${groupName}${qs(filters)}`),
  getLeaderboardActiveGroups: () => request('/leaderboard/active-groups'),
  getLeaderboardBoardSummary: () => request('/leaderboard/board-summary'),

  // Data entry
  createLeaderboardEntry: (data) => request('/leaderboard/entries', { method: 'POST', body: JSON.stringify(data) }),
  bulkUploadLeaderboardEntries: (data) => request('/leaderboard/entries/bulk', { method: 'POST', body: JSON.stringify(data) }),
  getLeaderboardEntries: (filters = {}) => request(`/leaderboard/data/entries${qs(filters)}`),

  // Group members (by group_name)
  getLeaderboardMembers: (groupName) => request(`/leaderboard/setup/members/${groupName}`),
  setLeaderboardMembers: (groupName, userIds) => request(`/leaderboard/setup/members/${groupName}`, { method: 'PUT', body: JSON.stringify({ user_ids: userIds }) }),
  getLeaderboardKpiMembers: (kpiId) => request(`/leaderboard/kpi-members/${kpiId}`),

  // ── Requests (Refund / No Charge / Take Back / …) ──────────
  getRequests: (filters = {}) => request(`/requests${qs(filters)}`),
  getRequest: (id) => request(`/requests/${id}`),
  getRequestTimeline: (id) => request(`/requests/${id}/timeline`),
  getRequestStats: () => request('/requests/stats'),
  getRequestBucketStats: (filters = {}) => request(`/requests/bucket-stats${qs(filters)}`),
  getRequestTypes: () => request('/requests/types'),
  exportRequestsUrl: (filters = {}) => `/api/requests/export${qs(filters)}`,
  submitRequest: (formData) => request('/requests', { method: 'POST', body: formData }),
  updateRequest: (id, data) => request(`/requests/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRequest: (id) => request(`/requests/${id}`, { method: 'DELETE' }),
  startProcessingRequest: (id) => request(`/requests/${id}/start-processing`, { method: 'PATCH' }),
  processRequest: (id, data) => request(`/requests/${id}/process`, { method: 'PATCH', body: JSON.stringify(data) }),
  payRequest: (id, data) => request(`/requests/${id}/pay`, { method: 'PATCH', body: JSON.stringify(data) }),
  denyRequest: (id, reason) => request(`/requests/${id}/deny`, { method: 'PATCH', body: JSON.stringify({ denial_reason: reason }) }),
  exportRequests: (filters = {}) => request(`/requests/export${qs(filters)}`),
  // ── Sign Requests (scheduling layer — bucket rows live in /requests) ──
  getSignRequestConfig: () => request('/sign-requests/config'),
  getSignAvailability: (params = {}) => request(`/sign-requests/availability${qs(params)}`),
  submitSignRequest: (formData) => request('/sign-requests', { method: 'POST', body: formData }),
  updateSignRequest: (id, data) => request(`/sign-requests/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkImportSignRequests: (formData, commit = false) => request(
    `/sign-requests/bulk-import${commit ? '?commit=1' : ''}`,
    { method: 'POST', body: formData }
  ),
  // Phase 2 — schedule board + technician execution
  getSignSchedule: (date, crew_id) => request(`/sign-requests/schedule${qs({ date, crew_id })}`),
  getSignCoverage: () => request('/sign-requests/coverage'),
  getSignPropertiesLite: () => request('/sign-requests/properties-lite'),
  quickSignReport: (formData) => request('/sign-requests/quick-report', { method: 'POST', body: formData }),
  quickGateAccessReport: (formData) => request('/gate-access', { method: 'POST', body: formData }),
  completeSignDetails: (id, data) => request(`/sign-requests/${id}/details`, { method: 'POST', body: JSON.stringify(data) }),
  getSignMonth: (month) => request(`/sign-requests/schedule/month${qs({ month })}`),
  getSignDaySuggestions: (date, stop_order, crew_id) => request('/sign-requests/schedule/day-suggestions', {
    method: 'POST', body: JSON.stringify({ date, stop_order, crew_id }),
  }),
  buildSignDay: (date, region, crew_id) => request('/sign-requests/schedule/build-day', { method: 'POST', body: JSON.stringify({ date, region: region || undefined, crew_id }) }),
  applySignBuildDay: (date, request_ids, crew_id) => request('/sign-requests/schedule/build-day/apply', { method: 'POST', body: JSON.stringify({ date, request_ids, crew_id }) }),
  getSignGeocodeStatus: () => request('/sign-requests/geocode/status'),
  getSignGeocodeFailed: () => request('/sign-requests/geocode/failed'),
  fixSignGeocode: (id, body) => request(`/sign-requests/geocode/fix/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  manualSignGeocode: (id, body) => request(`/sign-requests/geocode/manual/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  startSignGeocodeBackfill: () => request('/sign-requests/geocode/backfill', { method: 'POST' }),
  addToSignDay: (request_id, date, crew_id) => request('/sign-requests/schedule/add', { method: 'POST', body: JSON.stringify({ request_id, date, crew_id }) }),
  moveSignCrew: (request_id, crew_id) => request('/sign-requests/schedule/move-crew', { method: 'POST', body: JSON.stringify({ request_id, crew_id }) }),
  cancelSignRequest: (request_id, body = {}) => request('/sign-requests/schedule/cancel', { method: 'POST', body: JSON.stringify({ request_id, ...body }) }),
  mergeSignRequests: (keep_id, merge_ids) => request('/sign-requests/schedule/merge', { method: 'POST', body: JSON.stringify({ keep_id, merge_ids }) }),
  removeFromSignDay: (request_id) => request('/sign-requests/schedule/remove', { method: 'POST', body: JSON.stringify({ request_id }) }),
  saveSignRoute: (data) => request('/sign-requests/route', { method: 'PUT', body: JSON.stringify(data) }),
  publishSignRoute: (date, crew_id) => request('/sign-requests/route/publish', { method: 'POST', body: JSON.stringify({ date, crew_id }) }),
  getMySignRoute: (params = {}) => request(`/sign-requests/route/mine${qs(params)}`),
  submitSignOutcome: (id, data) => request(`/sign-requests/${id}/outcome`, {
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data),
  }),
  // Phase 3 — AI ordering, config, supply, Sign History
  suggestSignOrder: (date, crew_id) => request('/sign-requests/route/suggest-order', { method: 'POST', body: JSON.stringify({ date, crew_id }) }),
  createSignTask: (data) => request('/sign-requests/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateSignTask: (id, data) => request(`/sign-requests/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteSignTask: (id) => request(`/sign-requests/tasks/${id}`, { method: 'DELETE' }),
  completeSignTask: (id, formData) => request(`/sign-requests/tasks/${id}/done`, { method: 'POST', body: formData }),
  signRainDay: (date, crew_id, task) => request('/sign-requests/schedule/rain-day', { method: 'POST', body: JSON.stringify({ date, crew_id, task }) }),
  getSignSetup: () => request('/sign-requests/setup'),
  saveSignSetup: (data) => request('/sign-requests/setup', { method: 'PUT', body: JSON.stringify(data) }),
  addSignBlackout: (data) => request('/sign-requests/setup/blackout', { method: 'POST', body: JSON.stringify(data) }),
  removeSignBlackout: (date) => request(`/sign-requests/setup/blackout/${date}`, { method: 'DELETE' }),
  getSignHistoryForProperty: (propertyId) => request(`/sign-requests/history/property/${propertyId}`),

  // ── Tagging Requests (scheduling layer — bucket rows live in /requests) ──
  getTaggingRequestConfig: () => request('/tagging-requests/config'),
  getTaggingEstimate: (property_id) => request(`/tagging-requests/estimate${qs({ property_id })}`),
  getTaggingAvailability: (params = {}) => request(`/tagging-requests/availability${qs(params)}`),
  submitTaggingRequest: (formData) => request('/tagging-requests', { method: 'POST', body: formData }),
  updateTaggingRequest: (id, data) => request(`/tagging-requests/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkImportTaggingRequests: (formData, commit = false) => request(
    `/tagging-requests/bulk-import${commit ? '?commit=1' : ''}`,
    { method: 'POST', body: formData }
  ),
  // Phase 2 — schedule board + technician execution
  getTaggingSchedule: (date) => request(`/tagging-requests/schedule${qs({ date })}`),
  getTaggingCoverage: () => request('/tagging-requests/coverage'),
  getTaggingMonth: (month) => request(`/tagging-requests/schedule/month${qs({ month })}`),
  getTaggingDaySuggestions: (date, stop_order) => request('/tagging-requests/schedule/day-suggestions', {
    method: 'POST', body: JSON.stringify({ date, stop_order }),
  }),
  buildTaggingDay: (date, region) => request('/tagging-requests/schedule/build-day', { method: 'POST', body: JSON.stringify({ date, region: region || undefined }) }),
  applyTaggingBuildDay: (date, request_ids) => request('/tagging-requests/schedule/build-day/apply', { method: 'POST', body: JSON.stringify({ date, request_ids }) }),
  getTaggingGeocodeStatus: () => request('/tagging-requests/geocode/status'),
  getTaggingGeocodeFailed: () => request('/tagging-requests/geocode/failed'),
  fixTaggingGeocode: (id, body) => request(`/tagging-requests/geocode/fix/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  manualTaggingGeocode: (id, body) => request(`/tagging-requests/geocode/manual/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  startTaggingGeocodeBackfill: () => request('/tagging-requests/geocode/backfill', { method: 'POST' }),
  addToTaggingDay: (request_id, date) => request('/tagging-requests/schedule/add', { method: 'POST', body: JSON.stringify({ request_id, date }) }),
  cancelTaggingRequest: (request_id, body = {}) => request('/tagging-requests/schedule/cancel', { method: 'POST', body: JSON.stringify({ request_id, ...body }) }),
  mergeTaggingRequests: (keep_id, merge_ids) => request('/tagging-requests/schedule/merge', { method: 'POST', body: JSON.stringify({ keep_id, merge_ids }) }),
  removeFromTaggingDay: (request_id) => request('/tagging-requests/schedule/remove', { method: 'POST', body: JSON.stringify({ request_id }) }),
  saveTaggingRoute: (data) => request('/tagging-requests/route', { method: 'PUT', body: JSON.stringify(data) }),
  publishTaggingRoute: (date) => request('/tagging-requests/route/publish', { method: 'POST', body: JSON.stringify({ date }) }),
  getMyTaggingRoute: (params = {}) => request(`/tagging-requests/route/mine${qs(params)}`),
  submitTaggingOutcome: (id, data) => request(`/tagging-requests/${id}/outcome`, {
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data),
  }),
  // Phase 3 — AI ordering, config, supply, Tagging History
  suggestTaggingOrder: (date) => request('/tagging-requests/route/suggest-order', { method: 'POST', body: JSON.stringify({ date }) }),
  getTaggingSetup: () => request('/tagging-requests/setup'),
  saveTaggingSetup: (data) => request('/tagging-requests/setup', { method: 'PUT', body: JSON.stringify(data) }),
  addTaggingBlackout: (data) => request('/tagging-requests/setup/blackout', { method: 'POST', body: JSON.stringify(data) }),
  removeTaggingBlackout: (date) => request(`/tagging-requests/setup/blackout/${date}`, { method: 'DELETE' }),
  getTaggingHistoryForProperty: (propertyId) => request(`/tagging-requests/history/property/${propertyId}`),
  // 7-day forecast strip (Sign + Tagging Schedule boards)
  getWeather: () => request('/weather'),
  setWeatherZip: (zip) => request('/weather/location', { method: 'PUT', body: JSON.stringify({ zip }) }),
  investigateRequest: (id, data) => request(`/requests/${id}/investigation`, {
    method: 'PATCH', body: JSON.stringify(data),
  }),

  // Legacy Refund-only aliases — kept so any not-yet-rebuilt caller still works
  getRefunds: (filters = {}) => request(`/requests${qs({ ...filters, type: 'refund' })}`),
  getRefund: (id) => request(`/requests/${id}`),
  getRefundStats: () => request('/requests/stats'),
  createRefund: (formData) => { formData.append('type', 'refund'); return request('/requests', { method: 'POST', body: formData }); },
  startProcessingRefund: (id) => request(`/requests/${id}/start-processing`, { method: 'PATCH' }),
  processRefund: (id, data) => request(`/requests/${id}/process`, { method: 'PATCH', body: JSON.stringify(data) }),
  payRefund: (id, data) => request(`/requests/${id}/pay`, { method: 'PATCH', body: JSON.stringify(data) }),
  denyRefund: (id, reason) => request(`/requests/${id}/deny`, { method: 'PATCH', body: JSON.stringify({ denial_reason: reason }) }),
  exportRefunds: (filters = {}) => request(`/requests/export${qs({ ...filters, type: 'refund' })}`),

  // ── Expense Tracking ────────────────────────────────────────
  getExpenses: (filters = {}) => request(`/expenses${qs(filters)}`),
  getExpense: (id) => request(`/expenses/${id}`),
  getExpenseStats: (filters = {}) => request(`/expenses/stats${qs(filters)}`),
  getStipendInfo: (userId) => request(`/expenses/stipend-info${userId ? `?user_id=${userId}` : ''}`),
  getStipends: () => request('/expenses/stipends'),
  setStipend: (data) => request('/expenses/stipends', { method: 'POST', body: JSON.stringify(data) }),
  createExpense: (formData) => request('/expenses', { method: 'POST', body: formData }),
  reviewExpense: (id, data) => request(`/expenses/${id}/review`, { method: 'PATCH', body: JSON.stringify(data) }),
  markExpensePaid: (id, data) => request(`/expenses/${id}/paid`, { method: 'PATCH', body: JSON.stringify(data) }),
  voidExpense: (id, void_reason) => request(`/expenses/${id}/void`, { method: 'PATCH', body: JSON.stringify({ void_reason }) }),
  unvoidExpense: (id) => request(`/expenses/${id}/void`, { method: 'PATCH', body: JSON.stringify({ unvoid: true }) }),
  batchMarkExpensesPaid: (data) => request('/expenses/batch-paid', { method: 'POST', body: JSON.stringify(data) }),
  getUnlinkedExpenses: () => request('/expenses/unlinked'),
  linkExpenseAccounts: (id, propertyIds) => request(`/expenses/${id}/link-accounts`, { method: 'POST', body: JSON.stringify({ property_ids: propertyIds }) }),
  unlinkExpenseAccount: (id, propertyId) => request(`/expenses/${id}/link-accounts/${propertyId}`, { method: 'DELETE' }),
  exportExpenses: (filters = {}) => request(`/expenses/export${qs(filters)}`),

  // ── Damage Fund (driver escrow ledger) ───────────────────────
  getMyDamageFund: () => request('/damage-fund/me'),
  getDamageFundSummary: () => request('/damage-fund/summary'),
  getDamageFundAccounts: (filters = {}) => request(`/damage-fund/accounts${qs(filters)}`),
  getDamageFundAccount: (id) => request(`/damage-fund/accounts/${id}`),
  getDamageFundTransactions: (id, filters = {}) => request(`/damage-fund/accounts/${id}/transactions${qs(filters)}`),
  addDamageFundTransaction: (id, data) => request(`/damage-fund/accounts/${id}/transactions`, { method: 'POST', body: JSON.stringify(data) }),
  reverseDamageFundTransaction: (txId) => request(`/damage-fund/transactions/${txId}/reverse`, { method: 'POST' }),
  createDamageFundAccount: (data) => request('/damage-fund/accounts', { method: 'POST', body: JSON.stringify(data) }),
  updateDamageFundAccount: (id, data) => request(`/damage-fund/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getDamageFundMappableUsers: () => request('/damage-fund/mappable-users'),
  getDamageFundDeductionDraft: (date) => request(`/damage-fund/deduction-batch/draft${date ? `?date=${date}` : ''}`),
  confirmDamageFundDeductionBatch: (data) => request('/damage-fund/deduction-batch/confirm', { method: 'POST', body: JSON.stringify(data) }),
  exportDamageFund: (filters = {}) => request(`/damage-fund/export${qs(filters)}`),
  importDamageFund: (formData, { commit = false, force = false } = {}) => request(
    `/damage-fund/import${qs({ commit, force })}`,
    { method: 'POST', body: formData }
  ),

  // ── New Accounts ─────────────────────────────────────────────
  getAccounts: (filters = {}) => request(`/accounts${qs(filters)}`),
  getAccount: (id) => request(`/accounts/${id}`),
  getAccountStats: () => request('/accounts/stats/summary'),
  createAccount: (formData) => request('/accounts', { method: 'POST', body: formData }),
  updateAccountStatus: (id, data) => request(`/accounts/${id}/status`, { method: 'PATCH', body: JSON.stringify(data) }),
  uploadAccountDocs: (id, formData) => request(`/accounts/${id}/docs`, { method: 'POST', body: formData }),

  // ── Property Info Sheets ────────────────────────────────────
  getPropertyInfoByAccount: (accountId) => request(`/property-info/by-account/${accountId}`),
  getPropertyInfo: (id) => request(`/property-info/${id}`),
  savePropertyInfo: (formData) => request('/property-info', { method: 'POST', body: formData }),

  // ── Service Agreements ──────────────────────────────────────
  getServiceAgreements: () => request('/service-agreements'),
  getServiceAgreement: (id) => request(`/service-agreements/${id}`),
  getServiceAgreementsByAccount: (accountId) => request(`/service-agreements/by-account/${accountId}`),
  sendServiceAgreement: (data) => request('/service-agreements/send', { method: 'POST', body: JSON.stringify(data) }),
  resendServiceAgreement: (id) => request(`/service-agreements/${id}/resend`, { method: 'POST' }),
  // Public (no auth)
  getPublicAgreement: (token) => request(`/public/agreements/${token}`),
  signPublicAgreement: (token, data) => request(`/public/agreements/${token}/sign`, { method: 'POST', body: JSON.stringify(data) }),

  // ── Notices ──────────────────────────────────────────────────
  generateNotices: (data) => request('/notices/generate', { method: 'POST', body: JSON.stringify(data) }),
  getNotices: (filters = {}) => request(`/notices${qs(filters)}`),
  getNoticesByAccount: (accountId) => request(`/notices/by-account/${accountId}`),
  searchNotices: (q) => request(`/notices/search?q=${encodeURIComponent(q)}`),
  searchProperties: (q) => request(`/notices/search-properties?q=${encodeURIComponent(q)}`),
  getNotice: (id) => request(`/notices/${id}`),
  updateNotice: (id, data) => request(`/notices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  createNoticeVersion: (id) => request(`/notices/${id}/new-version`, { method: 'POST' }),
  voidNotice: (id) => request(`/notices/${id}`, { method: 'DELETE' }),
  getNoticeHistory: (filters = {}) => request(`/notices/history${qs(filters)}`),
  getNoticeHistoryStats: (filters = {}) => request(`/notices/history/stats${qs(filters)}`),

  // ── Notification Subscriptions ───────────────────────────────
  getNotificationConfig: () => request('/notifications/config'),
  subscribeNotification: (data) => request('/notifications/subscribe', { method: 'POST', body: JSON.stringify(data) }),
  unsubscribeNotification: (data) => request('/notifications/unsubscribe', { method: 'DELETE', body: JSON.stringify(data) }),
  updateNotificationThreshold: (data) => request('/notifications/threshold', { method: 'PUT', body: JSON.stringify(data) }),

  // Rounds
  getLeaderboardRounds: (kpiId) => request(`/leaderboard/rounds/${kpiId}`),
  getLeaderboardSetupRounds: (kpiId) => request(`/leaderboard/setup/rounds/${kpiId}`),
  createLeaderboardRound: (data) => request('/leaderboard/setup/rounds', { method: 'POST', body: JSON.stringify(data) }),
  updateLeaderboardRound: (id, data) => request(`/leaderboard/setup/rounds/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLeaderboardRound: (id) => request(`/leaderboard/setup/rounds/${id}`, { method: 'DELETE' }),
  resetKpiEntries: (kpiId) => request(`/leaderboard/setup/kpis/${kpiId}/reset-entries`, { method: 'POST' }),
  resetLeaderboardKpi: (kpiId) => request(`/leaderboard/setup/kpis/${kpiId}/reset`, { method: 'DELETE' }),

  // ── SPOTIO Integration ────────────────────────────────────────
  getSpotioConfig: () => request('/integrations/spotio/config'),
  saveSpotioConfig: (data) => request('/integrations/spotio/config', { method: 'PUT', body: JSON.stringify(data) }),
  testSpotioConnection: (data) => request('/integrations/spotio/test', { method: 'POST', body: JSON.stringify(data || {}) }),
  discoverSpotioStages: () => request('/integrations/spotio/discover-stages', { method: 'POST' }),
  saveSpotioStageOverrides: (overrides) => request('/integrations/spotio/stage-overrides', { method: 'PUT', body: JSON.stringify({ overrides }) }),

  // ── Google Sheets Integration ─────────────────────────────────
  getGSheetsConfig: () => request('/integrations/google-sheets/config'),
  saveGSheetsConfig: (data) => request('/integrations/google-sheets/config', { method: 'PUT', body: JSON.stringify(data) }),
  testGSheets: () => request('/integrations/google-sheets/test', { method: 'POST' }),
  previewGSheets: () => request('/integrations/google-sheets/preview', { method: 'POST' }),
  syncGSheets: () => request('/integrations/google-sheets/sync', { method: 'POST' }),
  getGSheetsUsers: () => request('/integrations/google-sheets/users'),
  disconnectGSheets: () => request('/integrations/google-sheets/config', { method: 'DELETE' }),
  getSpotioUsers: () => request('/integrations/spotio/users'),
  saveSpotioUserMap: (mappings) => request('/integrations/spotio/user-map', { method: 'PUT', body: JSON.stringify({ mappings }) }),
  triggerSpotioSync: () => request('/integrations/spotio/sync', { method: 'POST' }),

  // ── Workflow Engine ──────────────────────────────────────────
  // Templates (admin)
  getWorkflowTemplates: (params) => request(`/workflows${params ? '?' + new URLSearchParams(params) : ''}`),
  getWorkflowHealth: (params) => request(`/workflows/health${params ? '?' + new URLSearchParams(params) : ''}`),
  getWorkflowTemplate: (id) => request(`/workflows/${id}`),
  createWorkflowTemplate: (data) => request('/workflows', { method: 'POST', body: JSON.stringify(data) }),
  updateWorkflowTemplate: (id, data) => request(`/workflows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getWorkflowStages: (workflowId) => request(`/workflows/${workflowId}/stages`),
  saveWorkflowStages: (workflowId, stages, force_move_to) => request(`/workflows/${workflowId}/stages`, {
    method: 'POST',
    body: JSON.stringify(force_move_to ? { stages, force_move_to } : { stages }),
  }),
  updateWorkflowStage: (stageId, data) => request(`/workflows/stages/${stageId}`, { method: 'PUT', body: JSON.stringify(data) }),
  saveWorkflowTransitions: (workflowId, transitions) => request(`/workflows/${workflowId}/transitions`, { method: 'POST', body: JSON.stringify({ transitions }) }),
  saveStageEscalations: (stageId, escalations) => request(`/workflows/stages/${stageId}/escalations`, { method: 'POST', body: JSON.stringify({ escalations }) }),
  reassignWorkflowOwners: (workflowId) => request(`/workflows/${workflowId}/reassign-owners`, { method: 'POST' }),
  getWorkflowSpawnTypes: () => request('/workflows/spawn-types'),
  getWorkflowNotifications: (workflowId) => request(`/workflows/${workflowId}/notifications`),
  saveWorkflowNotifications: (workflowId, notifications) => request(`/workflows/${workflowId}/notifications`, { method: 'POST', body: JSON.stringify({ notifications }) }),

  // Items
  getMyWork: (params) => request(`/workflow-items/my-work${params ? '?' + new URLSearchParams(params) : ''}`),
  getWorkflowItemCounts: () => request('/workflow-items/counts'),
  getWorkflowItems: (params) => request(`/workflow-items${params ? '?' + new URLSearchParams(params) : ''}`),
  getWorkflowItem: (id) => request(`/workflow-items/${id}`),
  lookupWorkflowItem: (sourceType, sourceId) => request(`/workflow-items/by-source/${sourceType}/${sourceId}`),
  getWorkflowTree: (id) => request(`/workflow-items/${id}/tree`),
  updateWorkflowItemSource: (id, fields) => request(`/workflow-items/${id}/source-fields`, { method: 'PATCH', body: JSON.stringify(fields) }),
  createWorkflowItem: (data) => request('/workflow-items', { method: 'POST', body: JSON.stringify(data) }),
  transitionWorkflowItem: (id, data) => request(`/workflow-items/${id}/transition`, { method: 'PATCH', body: JSON.stringify(data) }),
  updateWorkflowItemOwners: (id, userIds, stageId) => request(`/workflow-items/${id}/owners`, { method: 'PATCH', body: JSON.stringify({ user_ids: userIds, stage_id: stageId }) }),
  updateWorkflowItemSupervisors: (id, userIds) => request(`/workflow-items/${id}/supervisors`, { method: 'PATCH', body: JSON.stringify({ user_ids: userIds }) }),
  addWorkflowComment: (id, message) => request(`/workflow-items/${id}/comments`, { method: 'POST', body: JSON.stringify({ message }) }),
  addWorkflowSubtask: (id, data) => request(`/workflow-items/${id}/subtasks`, { method: 'POST', body: JSON.stringify(data) }),
  toggleWorkflowSubtask: (id) => request(`/workflow-items/subtasks/${id}/toggle`, { method: 'PATCH' }),
  uploadWorkflowAttachments: (itemId, formData) => request(`/workflow-items/${itemId}/attachments`, { method: 'POST', body: formData }),
  deleteWorkflowAttachment: (id) => request(`/workflow-items/attachments/${id}`, { method: 'DELETE' }),
  reassignWorkflowItem: (id, data) => request(`/workflow-items/${id}/reassign`, { method: 'POST', body: JSON.stringify(data) }),
  remindWorkflowItem: (id) => request(`/workflow-items/${id}/remind`, { method: 'POST' }),
  cancelOutsourceTask: (id) => request(`/outsourced-tasks/${id}`, { method: 'DELETE' }),
  getOutsourceRule: (sourceType) => request(`/outsourced-tasks/rules/${encodeURIComponent(sourceType)}`),
  saveOutsourceRule: (sourceType, data) => request(`/outsourced-tasks/rules/${encodeURIComponent(sourceType)}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Teams & Positions
  getTeams: () => request('/teams'),
  createTeam: (data) => request('/teams', { method: 'POST', body: JSON.stringify(data) }),
  updateTeam: (id, data) => request(`/teams/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // Team members
  getTeamMembers: (teamId) => request(`/teams/${teamId}/members`),
  addTeamMember: (teamId, user_id, is_manager = false) => request(`/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify({ user_id, is_manager }) }),
  updateTeamMember: (teamId, userId, is_manager) => request(`/teams/${teamId}/members/${userId}`, { method: 'PUT', body: JSON.stringify({ is_manager }) }),
  removeTeamMember: (teamId, userId) => request(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
  moveTeamMember: (teamId, userId, newTeamId) => request(`/teams/${teamId}/members/${userId}/move`, { method: 'POST', body: JSON.stringify({ new_team_id: newTeamId }) }),
  getPositions: () => request('/positions'),
  createPosition: (data) => request('/positions', { method: 'POST', body: JSON.stringify(data) }),
  updatePosition: (id, data) => request(`/positions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getSupervisionLinks: (positionId) => request(`/positions/${positionId}/supervises`),
  setSupervisionLinks: (positionId, links) => request(`/positions/${positionId}/supervises`, { method: 'PUT', body: JSON.stringify({ links }) }),

  // Issue Templates
  getIssueTemplates: () => request('/issue-templates'),
  getIssueTemplate: (id) => request(`/issue-templates/${id}`),
  createIssueTemplate: (data) => request('/issue-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateIssueTemplate: (id, data) => request(`/issue-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Issue Reporting
  getAvailableIssueTemplates: () => request('/issues/available-templates'),
  submitIssueReport: (data) => request('/issues/submit', { method: 'POST', body: JSON.stringify(data) }),

  // ── Dashboard (cross-domain aggregations for the landing page) ──
  getDashboardQueueStats: (region) =>
    request(`/dashboard/queue-stats${region ? `?region=${encodeURIComponent(region)}` : ''}`),
  getDashboardDriverStats: (userId) =>
    request(`/dashboard/driver-stats?user_id=${encodeURIComponent(userId || 'me')}`),
  getShiftPickupStatus: () => request('/shift-pickup/status'),
  pickupShift: () => request('/shift-pickup', { method: 'POST' }),
  cancelShiftPickup: () => request('/shift-pickup', { method: 'DELETE' }),
  getDashboardTowTotals: (groups) => {
    const qs = groups && groups.length ? `?groups=${encodeURIComponent(groups.join(','))}` : '';
    return request(`/dashboard/tow-totals${qs}`);
  },
  getDashboardWorkflowByPosition: (positionName, limit = 3) =>
    request(`/dashboard/workflow-by-position?position_name=${encodeURIComponent(positionName)}&limit=${limit}`),

  // ── Audit Module ──
  searchAuditProperties: (search) => request(`/sales/audits/properties${qs({ search })}`),
  createAuditDraft: () => request('/sales/audits/drafts', { method: 'POST', body: JSON.stringify({}) }),
  getMyAuditDrafts: () => request('/sales/audits/drafts/me'),
  getAuditDraft: (id) => request(`/sales/audits/drafts/${id}`),
  patchAuditDraft: (id, data) => request(`/sales/audits/drafts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAuditDraft: (id) => request(`/sales/audits/drafts/${id}`, { method: 'DELETE' }),
  uploadAuditFiles: (draftId, kind, files) => {
    const fd = new FormData();
    fd.append('kind', kind);
    for (const f of files) fd.append('files', f);
    return request(`/sales/audits/drafts/${draftId}/files`, { method: 'POST', body: fd });
  },
  deleteAuditFile: (draftId, fileId) =>
    request(`/sales/audits/drafts/${draftId}/files/${fileId}`, { method: 'DELETE' }),
  parseAuditRentRoll: (draftId) =>
    request(`/sales/audits/drafts/${draftId}/parse-rent-roll`, { method: 'POST', body: JSON.stringify({}) }),
  parseAuditVehicles: (draftId) =>
    request(`/sales/audits/drafts/${draftId}/parse-vehicles`, { method: 'POST', body: JSON.stringify({}) }),
  parseAuditMyVIP: (draftId) =>
    request(`/sales/audits/drafts/${draftId}/parse-myvip`, { method: 'POST', body: JSON.stringify({}) }),
  submitAuditDraft: (draftId) =>
    request(`/sales/audits/drafts/${draftId}/submit`, { method: 'POST', body: JSON.stringify({}) }),
  listAudits: (filters = {}) => request(`/sales/audits${qs(filters)}`),
  getAuditsByProperty: (propertyId) => request(`/sales/audits/by-property/${propertyId}`),
  getAudit: (id) => request(`/sales/audits/${id}`),
  getAuditStatus: (id) => request(`/sales/audits/${id}/status`),
  downloadAuditUrl: (id) => `${API_BASE}/sales/audits/${id}/download`,
  // Authed workbook download — fetches as blob with Authorization header
  // and triggers a save dialog. Plain <a href> doesn't pass JWT, so the
  // server returns "Authentication required". Pass the audit object so
  // we can build a sensible default filename if Content-Disposition
  // isn't present.
  downloadAuditWorkbook: async (audit) => {
    const res = await fetch(`${API_BASE}/sales/audits/${audit.id}/download`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const cd = res.headers.get('content-disposition') || '';
    const match = cd.match(/filename="([^"]+)"/);
    const propName = audit.property_name || audit.property_label || 'Audit';
    const fallback = `${propName}_Parking_Audit_${(audit.created_at || '').slice(0, 10)}.xlsx`
      .replace(/\s+/g, '_');
    const a = document.createElement('a');
    a.href = url;
    a.download = match ? match[1] : fallback;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  reassignAudit: (id, propertyId) =>
    request(`/sales/audits/${id}/reassign`, { method: 'POST', body: JSON.stringify({ property_id: propertyId }) }),
  deleteAudit: (id) => request(`/sales/audits/${id}`, { method: 'DELETE' }),
  listColumnMappings: () => request('/sales/audits/column-mappings'),
  saveColumnMapping: (data) =>
    request('/sales/audits/column-mappings', { method: 'POST', body: JSON.stringify(data) }),

  // ── Workflow Platform: Forms (Phase A1+A2) ────────────────────────
  listForms: () => request('/forms'),
  getForm: (id) => request(`/forms/${id}`),
  createForm: ({ name, description, schema }) =>
    request('/forms', { method: 'POST', body: JSON.stringify({ name, description, schema }) }),
  updateForm: (id, patch) =>
    request(`/forms/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteForm: (id) => request(`/forms/${id}`, { method: 'DELETE' }),
  listFormSubmissions: (id) => request(`/forms/${id}/submissions`),

  /**
   * Submit a form. `values` is the typed-out value map from the renderer.
   * `files` is { fieldKey: File } for any file fields. Multipart so the
   * single endpoint takes both. Returns { submission_id, workflow_item_id }.
   */
  submitForm: async (formId, values, files = {}) => {
    const fd = new FormData();
    fd.append('values', JSON.stringify(values));
    for (const [fieldKey, file] of Object.entries(files)) {
      if (file) fd.append(fieldKey, file);
    }
    const res = await fetch(`${API_BASE}/forms/${formId}/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: fd,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Submit failed (${res.status})`);
    return body;
  },

  /**
   * Authed download for a form attachment (reuses the PNG-round-trip pattern
   * verified end-to-end on the audit module).
   */
  downloadFormFile: async (fileId, filename = 'attachment') => {
    const res = await fetch(`${API_BASE}/forms/files/${fileId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const cd = res.headers.get('content-disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    const a = document.createElement('a');
    a.href = url;
    a.download = m ? m[1] : filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // ── Workflow Platform: per-module submittable list ───────────────
  listSubmittableWorkflows: (module) =>
    request(`/workflows/submittable?module=${encodeURIComponent(module)}`),

  // ── Dispatcher PINs ─────────────────────────────────────────────
  pinLogin: (pin) => request('/pins/login', { method: 'POST', body: JSON.stringify({ pin }) }),
  pinLogout: () => request('/pins/logout', { method: 'POST' }),
  listPinsForStation: (stationUserId) => request(`/pins/station/${stationUserId}`),
  listStations: () => request('/pins/stations/list'),
  listEligibleForPin: (stationUserId) => request(`/pins/eligible-users/${stationUserId}`),
  createPin: (body) => request('/pins', { method: 'POST', body: JSON.stringify(body) }),
  updatePin: (id, body) => request(`/pins/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetPin: (id, pin) => request(`/pins/${id}/reset-pin`, { method: 'POST', body: JSON.stringify({ pin }) }),
  createStation: (body) => request('/pins/stations', { method: 'POST', body: JSON.stringify(body) }),
  createEmployeeWithPin: (body) => request('/pins/create-employee', { method: 'POST', body: JSON.stringify(body) }),
  setUserManager: (userId, managerUserId) => request(`/pins/users/${userId}/manager`, { method: 'PATCH', body: JSON.stringify({ manager_user_id: managerUserId }) }),
  listManagers: () => request('/pins/managers/list'),

  // ── Time clock ──────────────────────────────────────────────────
  clockIn: () => request('/timeclock/clock-in', { method: 'POST' }),
  clockOut: () => request('/timeclock/clock-out', { method: 'POST' }),
  breakStart: () => request('/timeclock/break/start', { method: 'POST' }),
  breakEnd: () => request('/timeclock/break/end', { method: 'POST' }),
  clockStatus: () => request('/timeclock/status'),
  myHours: (period = 'current') => request(`/timeclock/my-hours?period=${encodeURIComponent(period)}`),
  activeByStation: (stationUserId) => request(`/timeclock/active-by-station/${stationUserId}`),
  activeByRegion: (region) => request(`/timeclock/active-by-region/${encodeURIComponent(region)}`),
  getClockEntry: (id) => request(`/timeclock/entry/${id}`),

  // Admin / manager
  listEntries: (opts) => request(`/timeclock/entries${qs(opts || {})}`),
  getEntryFull: (id) => request(`/timeclock/entry/${id}/full`),
  patchEntry: (id, body) => request(`/timeclock/entry/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEntry: (id, reason) => request(`/timeclock/entry/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  addPunch: (entryId, body) => request(`/timeclock/entry/${entryId}/punches`, { method: 'POST', body: JSON.stringify(body) }),
  patchPunch: (id, body) => request(`/timeclock/punches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePunch: (id, reason) => request(`/timeclock/punches/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  addDay: (body) => request('/timeclock/add-day', { method: 'POST', body: JSON.stringify(body) }),
  forcePunch: (body) => request('/timeclock/force-punch', { method: 'POST', body: JSON.stringify(body) }),
  whosIn: () => request('/timeclock/whos-in'),

  // ── Shift checklists ────────────────────────────────────────────
  listChecklistTemplates: (scope, region) => request(`/checklists/templates${qs({ scope, region })}`),
  createChecklistTemplate: (body) => request('/checklists/templates', { method: 'POST', body: JSON.stringify(body) }),
  updateChecklistTemplate: (id, body) => request(`/checklists/templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  completeChecklist: (body) => request('/checklists/complete', { method: 'POST', body: JSON.stringify(body) }),
  listChecklistCompletions: (entryId) => request(`/checklists/completions?entry_id=${entryId}`),

  // ── Payroll ─────────────────────────────────────────────────────
  payrollSettings: () => request('/payroll/settings'),
  updatePayrollSettings: (body) => request('/payroll/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  listPayPeriods: (status) => request(`/payroll/periods${qs({ status })}`),
  lockPayPeriod: (id) => request(`/payroll/periods/${id}/lock`, { method: 'POST' }),
  approvePayPeriod: (id) => request(`/payroll/periods/${id}/approve`, { method: 'POST' }),
  payrollAccrual: (id) => request(`/payroll/periods/${id}/accrual`),
  closePayPeriod: (id, force) => request(`/payroll/periods/${id}/close`, { method: 'POST', body: JSON.stringify({ force }) }),
  closeReadiness: (id) => request(`/payroll/periods/${id}/close-readiness`),
  managerApprovePeriod: (id, note) => request(`/payroll/periods/${id}/gm-approve`, { method: 'POST', body: JSON.stringify({ note }) }),
  managerRejectPeriod: (id, note) => request(`/payroll/periods/${id}/gm-reject`, { method: 'POST', body: JSON.stringify({ note }) }),
  finalizePayPeriod: (id, note) => request(`/payroll/periods/${id}/ceo-approve`, { method: 'POST', body: JSON.stringify({ note }) }),
  listPeriodApprovals: (id) => request(`/payroll/periods/${id}/approvals`),
  payPeriodSummary: (id) => request(`/payroll/periods/${id}/summary`),
  exportPayrollCsv: (id) => `/api/payroll/periods/${id}/export.csv`,
  payrollReviewQueue: () => request('/payroll/review-queue'),
  approveTimecard: (id, manager_notes) => request(`/payroll/entries/${id}/approve`, { method: 'POST', body: JSON.stringify({ manager_notes }) }),
  rejectTimecard: (id, manager_notes) => request(`/payroll/entries/${id}/reject`, { method: 'POST', body: JSON.stringify({ manager_notes }) }),
  editTimecard: (id, body) => request(`/payroll/entries/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // ── PTO ─────────────────────────────────────────────────────────
  ptoBalance: (userId) => request(`/pto/balance/${userId}`),
  createPtoRequest: (body) => request('/pto/requests', { method: 'POST', body: JSON.stringify(body) }),
  listPtoRequests: (status) => request(`/pto/requests${qs({ status })}`),
  approvePto: (id, manager_notes) => request(`/pto/requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ manager_notes }) }),
  denyPto: (id, manager_notes) => request(`/pto/requests/${id}/deny`, { method: 'POST', body: JSON.stringify({ manager_notes }) }),

  // ── Driver Chat ─────────────────────────────────────────────────
  listChatChannels: () => request('/driver-chat/channels'),
  listChatMessages: (channelId, before, limit = 50) => request(`/driver-chat/channels/${channelId}/messages${qs({ before, limit })}`),
  postChatMessage: (channelId, body, send_sms = true) => request(`/driver-chat/channels/${channelId}/messages`, { method: 'POST', body: JSON.stringify({ body, send_sms }) }),
  markChatRead: (channelId, last_read_message_id) => request(`/driver-chat/channels/${channelId}/read`, { method: 'POST', body: JSON.stringify({ last_read_message_id }) }),
  chatUnreadCounts: () => request('/driver-chat/unread-counts'),
  chatChannelPreview: (channelId) => request(`/driver-chat/channels/${channelId}/preview`),

  // ── Directory (DFW Property Directory — Sales tool) ─────────────
  getDirectoryMeta: () => request('/sales/directory'),
  searchDirectory: (q, limit) => request(`/sales/directory/search${qs({ q, limit })}`),
  getDirectoryProperty: (id) => request(`/sales/directory/properties/${encodeURIComponent(id)}`),
  setDirectoryApollo: (id, apollo_customer, note) =>
    request(`/sales/directory/properties/${encodeURIComponent(id)}/apollo`, {
      method: 'PATCH', body: JSON.stringify({ apollo_customer, note }),
    }),
  clearDirectoryApolloOverride: (id) =>
    request(`/sales/directory/properties/${encodeURIComponent(id)}/apollo`, {
      method: 'PATCH', body: JSON.stringify({ clear: true }),
    }),
  // ── FWAA Business Exchange (event intelligence board) ──────────
  getFwaaOverview: () => request('/sales/fwaa/overview'),
  getFwaaCompany: (id) => request(`/sales/fwaa/companies/${id}`),
  getFwaaAttendee: (id) => request(`/sales/fwaa/attendees/${id}`),
  claimFwaaAttendee: (id) => request(`/sales/fwaa/attendees/${id}/claim`, { method: 'POST' }),
  unclaimFwaaAttendee: (id) => request(`/sales/fwaa/attendees/${id}/claim`, { method: 'DELETE' }),
  getFwaaMyAppointments: () => request('/sales/fwaa/my'),
  setFwaaClaimCompleted: (id, completed) =>
    request(`/sales/fwaa/attendees/${id}/claim`, { method: 'PATCH', body: JSON.stringify({ completed }) }),
  addFwaaNote: (id, body) =>
    request(`/sales/fwaa/attendees/${id}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteFwaaNote: (noteId) => request(`/sales/fwaa/notes/${noteId}`, { method: 'DELETE' }),
  importFwaa: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request('/sales/fwaa/import', { method: 'POST', body: fd });
  },

  getDirectoryCompanies: () => request('/sales/directory/companies'),
  getDirectoryCompany: (slug) => request(`/sales/directory/companies/${encodeURIComponent(slug)}`),
  getDirectoryOpportunities: (kind) => request(`/sales/directory/opportunities/${encodeURIComponent(kind)}`),

  // Authenticated CSV download — server returns text/csv with Content-Disposition.
  // Bearer token is required (super_admin only), so we can't just <a href>.
  downloadDirectoryCsv: async (params) => {
    const token = localStorage.getItem('token');
    const url = `/api/sales/directory/export.csv${qs(params || {})}`;
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let msg = `Export failed (${res.status})`;
      try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const filename = m ? m[1] : 'directory-export.csv';
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl; a.download = filename; document.body.appendChild(a);
    a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return filename;
  },
};
